/* =========================================================================
   BlincSync — theme behaviour
   Vanilla ES2019+, no framework, no build step. Everything here is progressive
   enhancement: the pages render and the store is usable with this file blocked.

   The Next.js build used framer-motion + zustand for this layer. Neither
   survives the port, and neither is missed — the animations were all
   transform/opacity, which CSS does on the compositor for free, and the only
   state worth holding (cart, wishlist) belongs to Shopify and localStorage
   respectively.
   ========================================================================= */
(function () {
  'use strict';

  var config = {};
  try {
    config = JSON.parse(document.getElementById('ThemeConfig').textContent);
  } catch (e) {
    config = { routes: {}, strings: {} };
  }

  var routes = config.routes || {};
  var strings = config.strings || {};

  /* ------------------------------------------------------------- helpers */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Shopify money formatting. Cart AJAX responses return integer cents, and
   * `shop.money_format` is the merchant's own template — reusing it keeps
   * JS-rendered prices identical to the Liquid-rendered ones instead of
   * inventing a second, subtly different format.
   */
  function formatMoney(cents, format) {
    var value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    var template = format || config.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withCommas(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
      var centsPart = parts[1] ? (decimal || '.') + parts[1] : '';
      return dollars + centsPart;
    }

    var match = template.match(placeholder);
    if (!match) return template;

    var formatted;
    switch (match[1]) {
      case 'amount':
        formatted = withCommas(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = withCommas(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withCommas(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withCommas(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withCommas(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withCommas(value, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withCommas(value, 2, "'", '.');
        break;
      default:
        formatted = withCommas(value, 2);
    }

    return template.replace(placeholder, formatted);
  }

  /* --------------------------------------------------------------- toast */

  var toastEl = $('#Toast');
  var toastTimer;

  function toast(message) {
    if (!toastEl || !message) return;
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* --------------------------------------------------------- focus traps */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    function handler(event) {
      if (event.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    return function () {
      container.removeEventListener('keydown', handler);
    };
  }

  /* Scroll locking has to survive nesting — opening the search dialog from
     inside the cart drawer must not unlock the page when only one closes. */
  var lockCount = 0;
  function lockScroll() {
    if (lockCount === 0) document.body.style.overflow = 'hidden';
    lockCount++;
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = '';
  }

  /* --------------------------------------------------------------- theme */

  function initTheme() {
    var root = document.documentElement;

    function apply(theme) {
      root.setAttribute('data-theme', theme);
      try {
        localStorage.setItem('bs-theme', theme);
      } catch (e) {
        /* private browsing — the in-memory attribute still works */
      }
      $$('[data-theme-toggle]').forEach(function (btn) {
        btn.setAttribute(
          'aria-label',
          btn.getAttribute('data-label-' + (theme === 'dark' ? 'light' : 'dark')) || 'Switch theme'
        );
      });
    }

    $$('[data-theme-toggle]').forEach(function (btn) {
      on(btn, 'click', function () {
        apply(root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark');
      });
    });

    apply(root.getAttribute('data-theme') || 'light');
  }

  /* -------------------------------------------------------------- header */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    /* Sticky header state. Passive listener + rAF so scrolling never waits on
       a layout read. */
    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
      ticking = false;
    }
    on(
      window,
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();

    /* Mega menu — hover and focus on desktop, closed on Escape or mouse-out. */
    var mega = $('[data-mega]', header);
    var megaTrigger = $('[data-mega-trigger]', header);

    if (mega && megaTrigger) {
      var openMega = function () {
        mega.classList.add('is-open');
        megaTrigger.setAttribute('aria-expanded', 'true');
      };
      var closeMega = function () {
        mega.classList.remove('is-open');
        megaTrigger.setAttribute('aria-expanded', 'false');
      };

      on(megaTrigger, 'mouseenter', openMega);
      on(megaTrigger, 'focus', openMega);
      on(mega, 'mouseenter', openMega);
      on(header, 'mouseleave', closeMega);
      on(header, 'focusout', function (event) {
        if (!header.contains(event.relatedTarget)) closeMega();
      });
      on(document, 'keydown', function (event) {
        if (event.key === 'Escape') closeMega();
      });

      /* Touch devices get no hover, so the first tap opens the panel rather
         than following the link straight through. */
      on(megaTrigger, 'click', function (event) {
        if (window.matchMedia('(hover: none)').matches && !mega.classList.contains('is-open')) {
          event.preventDefault();
          openMega();
        }
      });
    }

    /* Mobile drawer */
    var toggle = $('[data-mobile-toggle]', header);
    var mobileNav = $('[data-mobile-nav]', header);

    if (toggle && mobileNav) {
      on(toggle, 'click', function () {
        var open = mobileNav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        $$('[data-icon-menu], [data-icon-close]', toggle).forEach(function (icon) {
          icon.hidden = open ? icon.hasAttribute('data-icon-menu') : icon.hasAttribute('data-icon-close');
        });
      });
    }
  }

  /* ------------------------------------------------------- announcements */

  function initAnnouncement() {
    var bar = $('[data-announcement]');
    if (!bar) return;

    var items = $$('.announcement__item', bar);
    if (items.length < 2) return;

    /* Don't auto-rotate content for reduced-motion users — they get the first
       message only, which is why it carries the most important promise. */
    if (prefersReducedMotion.matches) return;

    var index = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 4500;

    setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, interval);
  }

  /* ------------------------------------------------------ scroll reveals */

  function initReveals() {
    var targets = $$('[data-reveal]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ----------------------------------------------------------- hero tilt */

  function initHero() {
    var hero = $('[data-hero]');
    if (!hero || prefersReducedMotion.matches) return;
    if (window.matchMedia('(hover: none)').matches) return;

    var spotlight = $('.hero__spotlight', hero);
    var collage = $('.hero__collage-inner', hero);
    if (!spotlight && !collage) return;

    var frame;
    on(
      hero,
      'pointermove',
      function (event) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var rect = hero.getBoundingClientRect();
          var x = (event.clientX - rect.left) / rect.width;
          var y = (event.clientY - rect.top) / rect.height;

          if (spotlight) {
            spotlight.style.setProperty('--spot-x', (x * 100).toFixed(2) + '%');
            spotlight.style.setProperty('--spot-y', (y * 100).toFixed(2) + '%');
          }
          if (collage) {
            collage.style.setProperty('--tilt-x', (8 - y * 16).toFixed(2) + 'deg');
            collage.style.setProperty('--tilt-y', (x * 20 - 10).toFixed(2) + 'deg');
          }
        });
      },
      { passive: true }
    );

    on(hero, 'pointerleave', function () {
      if (collage) {
        collage.style.setProperty('--tilt-x', '0deg');
        collage.style.setProperty('--tilt-y', '0deg');
      }
    });
  }

  /* ------------------------------------------------------- stat counters */

  function initCounters() {
    var counters = $$('[data-counter]');
    if (!counters.length) return;

    /* Reduced motion renders the final figure directly. Animating to it and
       relying on a completion callback races the observer — the element has
       already painted "0" by then. */
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      counters.forEach(function (el) {
        el.textContent = el.getAttribute('data-counter') + (el.getAttribute('data-suffix') || '');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          observer.unobserve(el);

          var target = parseFloat(el.getAttribute('data-counter')) || 0;
          var suffix = el.getAttribute('data-suffix') || '';
          var duration = 1400;
          var start = performance.now();

          function step(now) {
            var progress = Math.min((now - start) / duration, 1);
            /* Same ease-out curve as the CSS token, approximated. */
            var eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.round(target * eased) + suffix;
            if (progress < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------- cart */

  var Cart = {
    drawer: null,
    overlay: null,
    releaseFocus: null,
    lastFocused: null,

    init: function () {
      this.drawer = $('[data-cart-drawer]');
      this.overlay = $('[data-overlay]');
      if (!this.drawer) return;

      var self = this;

      $$('[data-cart-open]').forEach(function (btn) {
        on(btn, 'click', function (event) {
          event.preventDefault();
          self.open();
        });
      });

      $$('[data-cart-close]').forEach(function (btn) {
        on(btn, 'click', function () {
          self.close();
        });
      });

      on(this.overlay, 'click', function () {
        self.close();
      });

      on(document, 'keydown', function (event) {
        if (event.key === 'Escape' && self.drawer.classList.contains('is-open')) self.close();
      });

      /* Delegated so re-rendered drawer contents keep working without
         rebinding every line item. */
      on(this.drawer, 'click', function (event) {
        var remove = event.target.closest('[data-line-remove]');
        if (remove) {
          event.preventDefault();
          self.change(remove.getAttribute('data-line-remove'), 0);
          return;
        }

        var step = event.target.closest('[data-qty-step]');
        if (step) {
          var input = $('input', step.parentNode);
          if (!input) return;
          var next = parseInt(input.value, 10) + parseInt(step.getAttribute('data-qty-step'), 10);
          if (next < 0) next = 0;
          self.change(input.getAttribute('data-line'), next);
        }
      });

      on(
        this.drawer,
        'change',
        function (event) {
          var input = event.target.closest('[data-line]');
          if (input) self.change(input.getAttribute('data-line'), parseInt(input.value, 10));
        },
        true
      );

      /* Quick-add and product forms. Intercepting submit keeps the shopper on
         the page; without JS the same form posts to /cart/add and Shopify
         redirects to the cart, which is a fine fallback. */
      $$('[data-product-form]').forEach(function (form) {
        on(form, 'submit', function (event) {
          event.preventDefault();
          self.add(form);
        });
      });
    },

    open: function () {
      if (!this.drawer) return;
      this.lastFocused = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      lockScroll();
      this.releaseFocus = trapFocus(this.drawer);

      var close = $('[data-cart-close]', this.drawer);
      if (close) close.focus();
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-open');
      unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastFocused) this.lastFocused.focus();
    },

    add: function (form) {
      var self = this;
      var button = $('[type="submit"]', form);
      var formData = new FormData(form);

      /* Ask for the drawer's section HTML in the same round trip. Two requests
         (add, then re-fetch the cart) put a visible gap between the click and
         the drawer showing the new line. */
      formData.append('sections', 'cart-drawer');

      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      }

      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (data) {
          self.renderSections(data.sections);
          self.refreshCount();
          self.open();
          toast(strings.addedToCart);
        })
        .catch(function (error) {
          toast((error && error.description) || strings.cartError);
        })
        .finally(function () {
          if (button) {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
          }
        });
    },

    change: function (key, quantity) {
      var self = this;
      var body = $('[data-cart-body]', this.drawer);
      if (body) body.classList.add('is-loading');

      fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: 'cart-drawer' })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          self.renderSections(data.sections);
          self.refreshCount(data.item_count);

          /* The cart page has its own markup, so a quantity change there needs
             a reload to recalculate totals and shipping. */
          if (document.body.classList.contains('template-cart')) window.location.reload();
        })
        .catch(function () {
          toast(strings.cartError);
        })
        .finally(function () {
          if (body) body.classList.remove('is-loading');
        });
    },

    /**
     * Swap in freshly rendered section HTML. The Section Rendering API returns
     * the whole section wrapper, so the inner node is extracted to avoid
     * nesting a second wrapper on every update.
     */
    renderSections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var target = document.getElementById('shopify-section-' + id) || $('[data-cart-content]');
        if (!target) return;
        var parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        var source = parsed.getElementById('shopify-section-' + id) || parsed.body.firstElementChild;
        if (source) target.innerHTML = source.innerHTML;
      });
    },

    refreshCount: function (count) {
      function paint(value) {
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = value;
          el.hidden = value < 1;
        });
      }

      if (typeof count === 'number') {
        paint(count);
        return;
      }

      fetch(routes.cart ? routes.cart + '.js' : '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          paint(cart.item_count);
        })
        .catch(function () {
          /* leave the badge as-is */
        });
    }
  };

  /* ------------------------------------------------------------- search */

  var Search = {
    dialog: null,
    input: null,
    results: null,
    releaseFocus: null,
    lastFocused: null,
    controller: null,

    init: function () {
      this.dialog = $('[data-search-dialog]');
      if (!this.dialog) return;

      this.input = $('[data-search-input]', this.dialog);
      this.results = $('[data-search-results]', this.dialog);
      var self = this;

      $$('[data-search-open]').forEach(function (btn) {
        on(btn, 'click', function (event) {
          event.preventDefault();
          self.open();
        });
      });

      $$('[data-search-close], [data-search-scrim]', this.dialog).forEach(function (el) {
        on(el, 'click', function () {
          self.close();
        });
      });

      on(document, 'keydown', function (event) {
        /* ⌘K / Ctrl-K opens search from anywhere, matching the header hint. */
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          self.open();
          return;
        }
        if (event.key === 'Escape' && self.dialog.classList.contains('is-open')) self.close();
      });

      on(
        this.input,
        'input',
        debounce(function () {
          self.query(self.input.value.trim());
        }, 220)
      );
    },

    open: function () {
      this.lastFocused = document.activeElement;
      this.dialog.classList.add('is-open');
      this.dialog.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.releaseFocus = trapFocus(this.dialog);
      if (this.input) this.input.focus();
    },

    close: function () {
      this.dialog.classList.remove('is-open');
      this.dialog.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastFocused) this.lastFocused.focus();
    },

    query: function (term) {
      if (!this.results) return;

      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }

      /* Abort the in-flight request on every keystroke — otherwise a slow
         early response can land after a fast later one and show stale hits. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var self = this;
      var url =
        (routes.predictiveSearch || '/search/suggest') +
        '?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=6&section_id=predictive-search';

      fetch(url, { signal: this.controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var section = parsed.querySelector('[data-predictive-results]');
          self.results.innerHTML = section ? section.innerHTML : '';
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') self.results.innerHTML = '';
        });
    }
  };

  /* ------------------------------------------------------------ wishlist */

  var Wishlist = {
    key: 'bs-wishlist',

    read: function () {
      try {
        return JSON.parse(localStorage.getItem(this.key)) || [];
      } catch (e) {
        return [];
      }
    },

    write: function (list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {
        /* quota or private mode — the in-page state is still correct */
      }
      this.paintCount(list.length);
    },

    paintCount: function (count) {
      $$('[data-wishlist-count]').forEach(function (el) {
        el.hidden = count < 1;
        el.textContent = count;
      });
    },

    init: function () {
      var self = this;
      var list = this.read();

      $$('[data-wishlist-toggle]').forEach(function (btn) {
        var handle = btn.getAttribute('data-wishlist-toggle');
        btn.setAttribute('aria-pressed', String(list.indexOf(handle) !== -1));

        on(btn, 'click', function (event) {
          event.preventDefault();
          event.stopPropagation();

          var current = self.read();
          var index = current.indexOf(handle);

          if (index === -1) {
            current.push(handle);
            btn.setAttribute('aria-pressed', 'true');
            toast(strings.wishlistAdded);
          } else {
            current.splice(index, 1);
            btn.setAttribute('aria-pressed', 'false');
            toast(strings.wishlistRemoved);
          }

          self.write(current);
        });
      });

      this.paintCount(list.length);

      /* The wishlist page ships every product card hidden and reveals the
         saved ones — a stored list can't be rendered by Liquid, which has no
         access to localStorage. */
      var page = $('[data-wishlist-page]');
      if (page) {
        var shown = 0;
        $$('[data-wishlist-item]', page).forEach(function (card) {
          var match = list.indexOf(card.getAttribute('data-wishlist-item')) !== -1;
          card.hidden = !match;
          if (match) shown++;
        });
        var empty = $('[data-wishlist-empty]', page);
        if (empty) empty.hidden = shown > 0;
        var grid = $('[data-wishlist-grid]', page);
        if (grid) grid.hidden = shown === 0;
      }
    }
  };

  /* ------------------------------------------------- recently viewed */

  function initRecentlyViewed() {
    var key = 'bs-recent';
    var handle = document.body.getAttribute('data-product-handle');

    if (handle) {
      try {
        var list = JSON.parse(localStorage.getItem(key)) || [];
        list = list.filter(function (item) {
          return item !== handle;
        });
        list.unshift(handle);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 8)));
      } catch (e) {
        /* nothing to do — the section simply stays empty */
      }
    }

    var section = $('[data-recently-viewed]');
    if (!section) return;

    var stored = [];
    try {
      stored = JSON.parse(localStorage.getItem(key)) || [];
    } catch (e) {
      stored = [];
    }

    var shown = 0;
    $$('[data-recent-item]', section).forEach(function (card) {
      var match = stored.indexOf(card.getAttribute('data-recent-item')) !== -1;
      var isCurrent = card.getAttribute('data-recent-item') === handle;
      card.hidden = !match || isCurrent || shown >= 4;
      if (!card.hidden) shown++;
    });

    section.hidden = shown === 0;
  }

  /* ------------------------------------------------------------ quantity */

  function initQuantity() {
    $$('[data-qty]').forEach(function (widget) {
      var input = $('input', widget);
      if (!input || input.hasAttribute('data-line')) return; /* cart lines go through Cart */

      $$('[data-qty-step]', widget).forEach(function (btn) {
        on(btn, 'click', function () {
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          var next = parseInt(input.value, 10) + parseInt(btn.getAttribute('data-qty-step'), 10);
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------ newsletter */

  /* Shopify's customer form posts and redirects with ?customer_posted=true.
     Reading that back is what turns a full page reload into a confirmation. */
  function initNewsletterState() {
    if (window.location.search.indexOf('customer_posted=true') === -1) return;
    var status = $('[data-newsletter-status]');
    if (status) {
      status.hidden = false;
      status.focus();
    }
  }

  /* ------------------------------------------------------------------ go */

  function init() {
    initTheme();
    initHeader();
    initAnnouncement();
    initReveals();
    initHero();
    initCounters();
    initQuantity();
    initRecentlyViewed();
    initNewsletterState();
    Cart.init();
    Search.init();
    Wishlist.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor tears down and re-renders one section at a time; without
     this, a section reloaded in the customizer loses all its behaviour. */
  document.addEventListener('shopify:section:load', function () {
    init();
  });

  /* Exposed for the product page script, which needs the same money format. */
  window.BlincSync = {
    formatMoney: formatMoney,
    toast: toast,
    config: config,
    cart: Cart
  };
})();
